import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { classifyMediaError, detectBrowser, getPreviewStream, MEDIA_ERROR_COPY, PERMISSION_HELP, stopStream, type MediaErrorKind } from '@/lib/media';
import { useFaceCheck } from '@/features/face/useFaceCheck';
import { FACE_PRIVACY } from '@/features/face/guard';
import { cn } from '@/lib/cn';

function FacePill({ video }: { video: HTMLVideoElement | null }) {
  const check = useFaceCheck(video, { enabled: !!video });
  if (check.error && !check.ready) return null;
  const ok = check.faceVisible;
  return (
    <span
      className={cn('absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold', ok ? 'bg-green text-night' : 'bg-night/60 text-bone')}
      role="status"
      data-testid="camera-face-status"
    >
      {!check.ready ? 'Checking…' : ok ? (
        <>
          <Icon name="check" size={14} strokeWidth={3} /> Face in frame
        </>
      ) : (
        'We can’t see your face'
      )}
    </span>
  );
}

export function PermissionHelp({ kind }: { kind: MediaErrorKind }) {
  const browser = detectBrowser();
  const help = PERMISSION_HELP[browser];
  const copy = MEDIA_ERROR_COPY[kind];
  return (
    <div className="rounded-card bg-surface p-5 text-left" role="alert" data-testid="permission-help">
      <p className="flex items-center gap-2 text-[17px] font-semibold text-danger">
        <Icon name="cam-off" size={20} className="shrink-0" /> {copy.title}
      </p>
      <p className="mt-1 text-[15px] text-muted">{copy.body}</p>
      {kind === 'denied' && (
        <>
          <p className="mt-4 text-[15px] font-semibold">How to fix it in {help.name}:</p>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-[15px] text-fg">
            {help.steps.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}

function useMicLevel(stream: MediaStream | null): number {
  const [level, setLevel] = useState(0);
  useEffect(() => {
    if (!stream || stream.getAudioTracks().length === 0) return;
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const src = ctx.createMediaStreamSource(stream);
    const an = ctx.createAnalyser();
    an.fftSize = 256;
    src.connect(an);
    const buf = new Uint8Array(an.frequencyBinCount);
    let raf = 0;
    const tick = () => {
      an.getByteTimeDomainData(buf);
      let sum = 0;
      for (const v of buf) sum += ((v - 128) / 128) ** 2;
      setLevel(Math.min(1, Math.sqrt(sum / buf.length) * 4));
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelAnimationFrame(raf);
      void ctx.close();
    };
  }, [stream]);
  return level;
}

export function CameraCheck({ onResult, faceCheck = false, privacy = 'full' }: { onResult?: (ok: boolean) => void; faceCheck?: boolean; /** `short`: one line about privacy (SPEC_V10 onboarding). */ privacy?: 'full' | 'short' }) {
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [err, setErr] = useState<MediaErrorKind | null>(null);
  const [busy, setBusy] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const level = useMicLevel(stream);
  const streamRef = useRef<MediaStream | null>(null);

  const start = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      const s = await getPreviewStream();
      stopStream(streamRef.current);
      streamRef.current = s;
      setStream(s);
      onResult?.(true);
    } catch (e) {
      setErr(classifyMediaError(e));
      onResult?.(false);
    } finally {
      setBusy(false);
    }
  }, [onResult]);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      setVideoEl(videoRef.current);
    }
  }, [stream]);

  useEffect(() => () => stopStream(streamRef.current), []);

  return (
    <div className="space-y-4" data-testid="camera-check">
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-card bg-raised sm:aspect-video">
        {stream ? (
          <video ref={videoRef} autoPlay playsInline muted className="mirror h-full w-full object-cover" data-testid="self-preview" />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
            <Icon name={err ? 'cam-off' : 'cam'} size={32} className="text-fg" />
            <p className="max-w-xs text-[15px] text-muted">{err ? 'No preview yet.' : 'Your browser will ask for permission. Only you can see this preview.'}</p>
            {!err && (
              <Button onClick={start} loading={busy} icon="cam" data-testid="camera-start">
                Turn on camera & mic
              </Button>
            )}
          </div>
        )}
        {stream && (
          <div className="absolute bottom-3 left-3 flex items-center gap-2 rounded-full bg-night/60 px-3 py-1.5 text-[13px] font-semibold text-bone">
            <Icon name="mic" size={16} />
            <span className="flex h-2 w-20 overflow-hidden rounded-full bg-bone/25" aria-label="Microphone level" role="meter" aria-valuenow={Math.round(level * 100)} aria-valuemin={0} aria-valuemax={100}>
              <span className="h-full bg-green transition-[width] duration-75" style={{ width: `${Math.round(level * 100)}%` }} />
            </span>
          </div>
        )}
        {stream && !faceCheck && (
          <span className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-green px-3 py-1.5 text-[13px] font-semibold text-night">
            <Icon name="check" size={14} strokeWidth={3} /> Looking good
          </span>
        )}
        {stream && faceCheck && <FacePill video={videoEl} />}
      </div>
      {faceCheck && privacy === 'short' && (
        <p className="flex items-start gap-2 text-[15px] text-muted" data-testid="face-privacy">
          <Icon name="shield" size={18} className="mt-0.5 shrink-0 text-fg" /> Your face check runs on your device. Calls are never recorded.
        </p>
      )}
      {faceCheck && privacy === 'full' && (
        <div className="flex gap-3 rounded-card bg-surface p-4" data-testid="face-privacy">
          <Icon name="shield" size={20} className="mt-0.5 shrink-0" />
          <div className="text-[15px]">
            <p className="font-semibold text-fg">{FACE_PRIVACY}</p>
            <p className="mt-1 text-muted">
              Before each match and during calls, your device checks that a face is in frame — that’s all. No recognition, no age or gender guessing. If we can’t see you for a few seconds, your camera pauses until you’re back.
            </p>
          </div>
        </div>
      )}
      {err && (
        <>
          <PermissionHelp kind={err} />
          <Button variant="outline" accent="paper" onClick={start} loading={busy} icon="flip">
            Try again
          </Button>
        </>
      )}
    </div>
  );
}
